import { GridColDef } from "@mui/x-data-grid-pro";
import {
  Box,
  GKClientDataGrid,
  IconButton,
  KebabMenuIcon,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  SystematicPlanRow,
  SystematicPlanStatus,
} from "store/features/MFOrders";
import { extraColumnsField } from "utils/helpers";
import { moneyCommaSeparator } from "utils";

interface Props {
  plans: SystematicPlanRow[];
  loading?: boolean;
}

const STATUS_STYLE: Record<
  SystematicPlanStatus,
  { bg: string; color: string; label: string }
> = {
  active: { bg: "#e8f3ec", color: "#15803d", label: "Active" },
  paused: { bg: "#f9f2dd", color: "#e6b532", label: "Paused" },
  cancelled: { bg: "#ececec", color: "#697586", label: "Cancelled" },
};

const StatusPill = ({ status }: { status: SystematicPlanStatus }) => {
  const styles = STATUS_STYLE[status] ?? {
    bg: "#ececec",
    color: "#697586",
    label: status,
  };
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: styles.bg,
        borderRadius: "6px",
        display: "inline-flex",
        px: "10px",
        py: "4px",
      }}
    >
      <Typography
        color={styles.color}
        fontSize={10}
        fontWeight={600}
        sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
      >
        {styles.label}
      </Typography>
    </Box>
  );
};

const toNum = (s?: string | null) => {
  if (s == null || s === "") return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

const SystematicPlansTable = ({ plans, loading = false }: Props) => {
  const navigate = useNavigate();

  const columns: GridColDef<SystematicPlanRow>[] = [
    {
      ...extraColumnsField("Creation Date & Time", "createdAt", 180, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {params?.row?.createdAt
            ? dayjs(params.row.createdAt).format("DD/MM/YYYY, hh:mm A")
            : "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Portfolio Name", "portfolioName", 200, 0),
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
            {params?.row?.portfolioName ?? "-"}
          </Typography>
          <Typography color="var(--neutral-500)" fontSize={11} fontWeight={500}>
            {params?.row?.portfolioCode ?? ""}
          </Typography>
        </Box>
      ),
    },
    {
      ...extraColumnsField("Scheme Name", "schemeName", 240, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography
          color="var(--black-900)"
          fontSize={13}
          fontWeight={500}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params?.row?.schemeName ?? "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Folio No.", "folioNumber", 130, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {params?.row?.folioNumber ?? "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Plan Type", "planType", 110, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography
          color="var(--black-900)"
          fontSize={13}
          fontWeight={600}
          sx={{ textTransform: "uppercase" }}
        >
          {params?.row?.planType ?? "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Amount", "amount", 130, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const v = toNum(params?.row?.amount);
        return (
          <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
            {v != null ? `₹${moneyCommaSeparator(v)}` : "-"}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("Next Inst. Date", "nextInstallmentDate", 160, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {params?.row?.nextInstallmentDate
            ? dayjs(params.row.nextInstallmentDate).format("DD/MM/YYYY")
            : "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Last Inst. Date", "lastInstallmentDate", 160, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {params?.row?.lastInstallmentDate
            ? dayjs(params.row.lastInstallmentDate).format("DD/MM/YYYY")
            : "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Status", "status", 110, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        params?.row?.status ? <StatusPill status={params.row.status} /> : null,
    },
    {
      ...extraColumnsField("Action", "_action", 80, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: () => (
        <IconButton
          sx={{ p: "4px" }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <KebabMenuIcon height={16} width={16} />
        </IconButton>
      ),
    },
  ];

  return (
    <GKClientDataGrid
      name="mfSystematicPlans"
      loading={loading}
      columns={columns}
      rows={plans || []}
      getRowId={(row) => row.id}
      maxHeight={"calc(100vh - 327px)"}
      columnsButton={false}
      onRowClick={(params) => navigate(`/app/orders/plans/${params.row.id}`)}
      sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
    />
  );
};

export default SystematicPlansTable;
